import { EditorConfig, LexicalNode, NodeKey, LexicalEditor, ElementFormatType } from 'lexical';
import { ListNode, ListType, SerializedListNode } from '@lexical/list';
import { v4 as uuidv4 } from 'uuid';
import { createCommentableNode } from '../CommentableMixin';

import ReactionArticleIcon from '../../../../assets/reaction-article-icon.svg';

//   import './styles.css';

export type SerializedCommentableListNode = SerializedListNode & {
  type: 'commentable-list';
  version: 1;
  nodeId: string;
  commentCount: number;
  reactionCount: number;
  nodeData: {
    reactionCount: number;
    isReacted: boolean;
    isCommented: boolean;
    reactedType?: string;
    comment?: {
      isCommented: boolean;
      commentCount: number;
    };
  };
};

const CommentableListNodeBase = createCommentableNode(ListNode);

export class CommentableListNode extends CommentableListNodeBase {
  __nodeId: string;
  __commentCount: number;
  __reactionCount: number;
  __nodeData: any = {};

  static getType(): string {
    return 'commentable-list';
  }

  static clone(node: CommentableListNode): CommentableListNode {
    return new CommentableListNode(
      node.__listType,
      node.__start,
      node.__key,
      node.__nodeId,
      node.__commentCount,
      node.__reactionCount,
      node.__nodeData
    );
  }

  constructor(
    listType?: ListType,
    start?: number,
    key?: NodeKey,
    nodeId?: string,
    commentCount: number = 0,
    reactionCount: number = 0,
    nodeData: any = {}
  ) {
    super(listType, start, key);
    this.__nodeId = nodeId || uuidv4();
    this.__commentCount = commentCount;
    this.__reactionCount = reactionCount;
    this.__nodeData = nodeData;
  }

  updateDOM(prevNode: CommentableListNode, dom: HTMLElement, config: EditorConfig): boolean {
    const baseInstance = this as any;

    const currentNodeData = this.getNodeData();
    const prevNodeData = prevNode.getNodeData();

    const reactionButton = dom.querySelector('.reactions-button') as HTMLElement;
    const commentButton = dom.querySelector('.comment-button') as HTMLElement;
    const countButton = dom.querySelector('.reaction-count-button') as HTMLElement;

    if (!reactionButton || !commentButton || !countButton) {
      return true;
    }

    // Update Reaction Button
    const isReacted = currentNodeData?.isReacted && currentNodeData?.reactedType;
    const wasReacted = prevNodeData?.isReacted && prevNodeData?.reactedType;
    const reactionTypeChanged = currentNodeData?.reactedType !== prevNodeData?.reactedType;

    if (isReacted === wasReacted || reactionTypeChanged) {
      const reactionState = reactionButton.querySelector('.reaction-state') as HTMLElement;
      if (reactionState) {
        reactionState.innerHTML = ''; // Clear only the reaction state
        reactionButton.classList.remove('reacted');

        if (isReacted) {
          const selectedEmoji = baseInstance.getReactionIconByType(currentNodeData.reactedType);
          if (selectedEmoji) {
            reactionButton.classList.add('reacted');
            const img = document.createElement('img');
            img.classList.add('image-reacted');
            img.src = selectedEmoji.icon;
            img.alt = selectedEmoji.textDisplay;
            reactionState.appendChild(img);
            const span = document.createElement('span');
            span.classList.add('text-reacted');
            span.textContent = selectedEmoji.textDisplay;
            reactionState.appendChild(span);
          }
        } else {
          const img = document.createElement('img');
          img.classList.add('reaction-icon');
          img.src = ReactionArticleIcon;
          img.alt = 'Reaction Icon';
          reactionState.appendChild(img);
          const plus = document.createElement('span');
          plus.classList.add('reaction-plus');
          plus.textContent = '+';
          reactionState.appendChild(plus);
        }
      }
    }

    const isCommented = currentNodeData?.comment?.isCommented || false;
    const wasCommented = prevNodeData?.comment?.isCommented || false;
    if (isCommented !== wasCommented) {
      const commentIcon = commentButton.querySelector('.comment-icon') as HTMLImageElement;
      if (commentIcon) {
        if (isCommented) {
          commentButton.style.backgroundColor = '#4CAF50';
          commentIcon.style.filter = 'brightness(0) invert(1)';
        } else {
          commentButton.style.backgroundColor = '';
          commentIcon.style.filter = '';
        }
      }
    }

    const currentCount = baseInstance.getTotalCount();
    const prevCount = (prevNode as any).getTotalCount();
    if (currentCount !== prevCount) {
      const countText = countButton.querySelector('.reaction-count-text') as HTMLElement;
      if (countText) {
        countText.textContent = currentCount;
      }
    }

    return false;
  }

  exportJSON(): SerializedCommentableListNode {
    const baseJSON = super.exportJSON() as any;
    return {
      ...baseJSON,
      type: 'commentable-list',
      version: 1,
      nodeId: this.__nodeId,
      commentCount: this.__commentCount,
      reactionCount: this.__reactionCount,
      nodeData: this.__nodeData
    };
  }

  static importJSON(serializedNode: SerializedCommentableListNode): CommentableListNode {
    const { listType, start, direction, indent, children } = serializedNode;

    const format = serializedNode.format as unknown as ElementFormatType;

    const commentableProps = serializedNode as SerializedCommentableListNode;

    const node = new CommentableListNode(
      listType,
      start,
      undefined,
      commentableProps.nodeId,
      commentableProps.commentCount || 0,
      commentableProps.reactionCount || 0,
      commentableProps.nodeData || {}
    );

    node.setFormat(format);
    node.setIndent(indent);
    node.setDirection(direction);

    return node;
  }
}

export function $createCommentableListNode(
  listType: ListType = 'bullet',
  start: number = 1,
  nodeId?: string,
  commentCount: number = 0
): CommentableListNode {
  return new CommentableListNode(listType, start, undefined, nodeId, commentCount, 0, {});
}

export function $isCommentableListNode(
  node: LexicalNode | null | undefined
): node is CommentableListNode {
  return node instanceof CommentableListNode;
}
